import { useCallback, useEffect, useMemo, useState } from 'react';

import Cookies, { type Cookie, type CookieSetOptions } from 'universal-cookie';
import { useDidMount } from './hooks/useDidMount';
import { format, parse } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { SharedGameFull } from '@/api';

export const useLocalStorage = (key: string, initialValue: any) => {
    // State to store our value
    // Pass initial state function to useState so logic is only executed once
    const [storedValue, setStoredValue] = useState(() => {
        try {
            // Get from local storage by key
            const item = window.localStorage.getItem(key);
            // Parse stored json or if none return initialValue
            return item ? JSON.parse(item) : initialValue;
        } catch {
            return initialValue;
        }
    });
    // Return a wrapped version of useState's setter function that ...
    // ... persists the new value to localStorage.
    const setValue = (value: any) => {
        try {
            // Allow value to be a function, so we have same API as useState
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            // Save state
            setStoredValue(valueToStore);
            // Save to local storage
            window.localStorage.setItem(key, JSON.stringify(valueToStore));
        } catch {
            // TODO
        }
    };
    return [storedValue, setValue];
};

export const useSessionStorage = (key: string, initialValue: any) => {
    const [storedValue, setStoredValue] = useState(() => {
        try {
            const item = window.sessionStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch {
            return initialValue;
        }
    });

    const setValue = (value: any) => {
        try {
            const valueToStore = value instanceof Function ? value(storedValue) : value;
            setStoredValue(valueToStore);
            window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
        } catch {
            // TODO
        }
    };

    return [storedValue, setValue];
};

export const useCookie = (key: string, initialValue: any) => {
    const cookies = useMemo(() => new Cookies(null, { path: '/' }), []);

    const [value, setValue] = useState(() => {
        const cookie = cookies.get(key);
        if (cookie) return cookie;
        cookies.set(key, initialValue);
        return initialValue;
    });

    const updateCookie = useCallback(
        (newValue: Cookie, options?: CookieSetOptions) => {
            cookies.set(key, newValue, options);
            setValue(newValue);
        },
        [cookies, key]
    );

    const deleteCookie = useCallback(() => {
        cookies.remove(key);
        setValue(null);
    }, [cookies, key]);

    return [value, updateCookie, deleteCookie];
};

const sortingType = (el: any) => {
    if (+el) return +el;
    const maybeDate = new Date(el);
    if (!isNaN(maybeDate.getTime())) return maybeDate;
    return el;
};

export const sort = (elA: any, elB: any) => {
    if (sortingType(elA) > sortingType(elB)) return 1;
    if (sortingType(elA) < sortingType(elB)) return -1;
    return 0;
};

export const randomId = (length = 6) =>
    Math.random()
        .toString(36)
        .substring(2, length + 2);

export const formatKilo = (n: number): string => numberSeparate(n, '\u00A0');

export const numberSeparate = (x: number, s: string): string => {
    if (!x) return '';
    return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, s);
};

export const rank = (r: number): string => {
    if (r < 2100) {
        return Math.ceil((2100 - r) / 75) + 'k';
    }
    return Math.floor((r - 2000) / 100) + 'd';
};

export const dateMonthYear = (date: string): string => {
    const d = new Date(date);
    return `${padZero(d.getMonth() + 1, 2)}/${d.getFullYear()}`;
};

export const padZero = (num: number, size: number): string => {
    let s = num + '';
    while (s.length < size) s = '0' + s;
    return s;
};

export const formatNumberWithSign = (number: number) => {
    if (number > 0) return `+${number}`;
    if (number === 0) return `=`;
    return `-${number}`;
};

export const createOptionsForSelectYears = (start: number, end?: number) => {
    if (end === undefined) end = new Date().getFullYear();

    const years = [];

    for (let year = end; year >= start; year--) {
        years.push({ value: year.toString(), label: year.toString() });
    }

    return years;
};

export const yearToMinAndMaxDate = (year: string) => {
    if (!year) return null;
    return { minStartDate: `${year}-01-01`, maxStartDate: `${year}-12-31` };
};

export const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // месяцы начинаются с 0
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

export const useIsMobile = (trigger: number = 768) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= trigger);

    useDidMount(() => {
        const handle = () => {
            setIsMobile(window.innerWidth <= trigger);
        };
        window.addEventListener('resize', handle);

        return () => {
            window.removeEventListener('resize', handle);
        };
    });

    return isMobile;
};

export const useScreenOrientation = () => {
    const getOrientation = () => (window.matchMedia('(orientation: landscape)').matches ? 'landscape' : 'portrait');
    const [orientation, setOrientation] = useState<'landscape' | 'portrait'>(getOrientation());

    useDidMount(() => {
        const handleResize = () => {
            setOrientation(getOrientation());
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    });
    return orientation;
};

export const getNounForm = (number: number, one: string, few: string, many: string) => {
    number = Math.abs(number) % 100;
    const n1 = number % 10;

    if (number > 10 && number < 20) {
        return many;
    }
    if (n1 > 1 && n1 < 5) {
        return few;
    }
    if (n1 === 1) {
        return one;
    }
    return many;
};

/**
 * Formats a date range for streaks.
 * If both dates are in the same year, omits the year from the START date.
 * @param {string | undefined} start - The start date.
 * @param {string | undefined} end - The end date.
 * @returns {string} Formatted date range.
 */
export const formatStreakDateRange = (start: string | undefined, end: string | undefined): string => {
    const startDate = start ? new Date(start) : new Date();
    const endDate = end ? new Date(end) : new Date();

    const sameYear = startDate.getFullYear() === endDate.getFullYear();

    const startFormat = sameYear ? 'dd.MM' : 'dd.MM.yyyy';
    const endFormat = 'dd.MM.yyyy';

    try {
        return `${format(startDate, startFormat)} - ${format(endDate, endFormat)}`;
    } catch {
        return '-';
    }
};

/**
 * Formats a date string to a localized format.
 * @param {string | null} date - The date string to format.
 * @param {string} language - The language code ('ru' for Russian, 'en' for English).
 * @returns {string} Formatted date in 'dd.MM.yyyy' format or '-' if date is null.
 */
export const formatDate = (date: string | null, language: string = 'ru'): string => {
    if (!date) return '-';
    let options = {};
    if (language === 'ru') options = { locale: ru };
    else options = { locale: enUS };
    try {
        return format(new Date(date), 'dd.MM.yyyy', options);
    } catch {
        return '-';
    }
};

export const formatYearMonth = (yearMonth: string, language: string): string => {
    const date = parse(yearMonth, 'yyyy-MM', new Date());
    let options = {};
    if (language === 'ru') options = { locale: ru };
    else options = { locale: enUS };
    return format(date, 'LLLL yyyy', options);
};

export const copyToClipbord = (text: string, setIsOpenTooltip: React.Dispatch<React.SetStateAction<boolean>>) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
            setIsOpenTooltip(true);
            setTimeout(() => setIsOpenTooltip(false), 1000);
        });
    } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setIsOpenTooltip(true);
        setTimeout(() => setIsOpenTooltip(false), 1000);
    }
};

export const formatAmount = (amount: number, lang: string = 'en') => {
    const suffixes = {
        ru: ['', ' тыс', ' млн', ' млрд'],
        en: ['', 'k', 'M', 'B'],
    };

    let i = 0;

    type SupportedLanguage = keyof typeof suffixes;

    function isSupportedLanguage(lang: string): lang is SupportedLanguage {
        return lang in suffixes;
    }

    const currentLang: SupportedLanguage = isSupportedLanguage(lang) ? lang : 'en';

    // Пока число больше или равно 1000, делим его и увеличиваем индекс суффикса
    while (amount >= 1000 && i < suffixes[currentLang].length - 1) {
        amount /= 1000;
        i++;
    }

    // Округляем до 3 значащих цифр, чтобы всегда оставались 3 символа (например, 1.12K)
    const roundedNum = amount.toFixed(amount >= 100 ? 0 : amount >= 10 ? 1 : 2);

    // Возвращаем число с соответствующим суффиксом
    return { num: parseInt(roundedNum), suffix: suffixes[currentLang][i] };
};

export const countPlayers = (status: string, players?: number, registered?: number): number =>
    status === 'ended' || !!players ? players || 0 : registered || 0;

export const bytesToSize = (bytes: number, lang = 'en', precision = 2) => {
    const units = {
        ru: ['Байт', 'КБ', 'МБ', 'ГБ', 'ТБ'],
        en: ['B', 'KB', 'MB', 'GB', 'TB'],
    };

    type SupportedLanguage = keyof typeof units;

    function isSupportedLanguage(lang: string): lang is SupportedLanguage {
        return lang in units;
    }

    const currentLang: SupportedLanguage = isSupportedLanguage(lang) ? lang : 'en';
    if (bytes === 0) return '0 Байт';
    const factor = 1024;
    const index = Math.floor(Math.log(bytes) / Math.log(factor));
    return (bytes / Math.pow(factor, index)).toFixed(precision) + ' ' + units[currentLang][index];
};

export const getRankValue = (rank: string) => {
    const match = rank.match(/(\d+)([kdp])/);
    if (!match) return 0;

    const [, numStr, type] = match;
    const num = parseInt(numStr, 10);

    switch (type) {
        case 'k':
            return 3000 - num;
        case 'd':
            return 4000 + num;
        case 'p':
            return 5000 + num;
        default:
            throw new Error(`Unexpected rank type: ${type}`);
    }
};

const sortCondition = (game: SharedGameFull) => new Date(game.date!).getTime() * 10 + (game.round || 0);

export const sortGames = ({ games, sort }: { games: SharedGameFull[]; sort: 'asc' | 'desc' }) =>
    games.sort((a, b) => (sort === 'asc' ? sortCondition(a) - sortCondition(b) : sortCondition(b) - sortCondition(a)));

export const deepEqual = (obj1: any, obj2: any) => {
    // Сравнение примитивов
    if (obj1 === obj2) return true;

    // Проверка на null и типы данных
    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return false;
    }

    // Сравнение массивов
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) return false;
        for (let i = 0; i < obj1.length; i++) {
            if (!deepEqual(obj1[i], obj2[i])) return false;
        }
        return true;
    }

    // Сравнение объектов
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
        if (!keys2.includes(key)) return false;
        if (!deepEqual(obj1[key], obj2[key])) return false;
    }

    return true;
};

/**
 * Вычисляет максимальное количество карточек, которые могут поместиться в высоту viewport.
 * @param cardHeight - Высота одной карточки в пикселях
 * @param gap - Отступ между карточками в пикселях (по умолчанию: 16)
 * @param bottomOffset - Отступ снизу в пикселях (по умолчанию: 45 для desktop, 0 для mobile)
 * @param topOffset - Отступ сверху в пикселях (по умолчанию: 0)
 * @returns Количество карточек, которые могут поместиться в viewport
 */
export const calculateVisibleCardsCount = (
    cardHeight: number,
    gap: number = 16,
    bottomOffset: number = 45,
    topOffset: number = 0
): number => {
    const viewportHeight = window.innerHeight;
    const availableHeight = viewportHeight - topOffset - bottomOffset;

    if (availableHeight <= 0) {
        return 0;
    }

    // Если даже одна карточка не помещается, возвращаем 0
    if (cardHeight > availableHeight) {
        return 0;
    }

    // Вычисляем сколько карточек помещается: (availableHeight + gap) / (cardHeight + gap)
    // Это учитывает отступы между карточками
    const cardsCount = Math.floor((availableHeight + gap) / (cardHeight + gap));

    return Math.max(0, cardsCount);
};

/**
 * Хук, который вычисляет максимальное количество карточек, которые могут поместиться в viewport,
 * и обновляет значение при изменении размера окна.
 * Для мобильной версии всегда возвращает 4 карточки.
 * @param cardHeight - Высота одной карточки в пикселях
 * @param gap - Отступ между карточками в пикселях (по умолчанию: 16)
 * @param bottomOffset - Отступ снизу в пикселях (по умолчанию: 45 для desktop, 0 для mobile)
 * @param topOffset - Отступ сверху в пикселях (по умолчанию: 0)
 * @param isMobile - Флаг мобильной версии (если true, возвращает 4)
 * @returns Количество карточек, которые могут поместиться в viewport
 */
export const useVisibleCardsCount = (
    cardHeight: number,
    gap: number = 16,
    bottomOffset: number = 45,
    topOffset: number = 0,
    isMobile: boolean = false
): number => {
    // Всегда вызываем хуки в одном порядке, чтобы не нарушать правила React
    const [cardsCount, setCardsCount] = useState(() =>
        isMobile ? 4 : calculateVisibleCardsCount(cardHeight, gap, bottomOffset, topOffset)
    );

    // Обновляем значение при изменении параметров
    useEffect(() => {
        if (isMobile) {
            setCardsCount(4);
        } else {
            setCardsCount(calculateVisibleCardsCount(cardHeight, gap, bottomOffset, topOffset));
        }
    }, [isMobile, cardHeight, gap, bottomOffset, topOffset]);

    // Слушаем изменение размера окна для desktop версии
    useEffect(() => {
        // Для мобильной версии не нужно слушать resize, так как всегда возвращаем 4
        if (isMobile) {
            return;
        }

        const handleResize = () => {
            setCardsCount(calculateVisibleCardsCount(cardHeight, gap, bottomOffset, topOffset));
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isMobile, cardHeight, gap, bottomOffset, topOffset]);

    // Для мобильной версии всегда возвращаем 4 карточки
    if (isMobile) {
        return 4;
    }

    return cardsCount;
};

export const convertBlob = async (blob: Blob): Promise<string> => {
    const arrayBuffer = await blob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);
    return btoa(Array.from(uint8Array, (byte) => String.fromCharCode(byte)).join(''));
};
